// apps/web/src/lib/registerAudioSW.ts
// ─────────────────────────────────────────────────────────────────────────
// Registers the audio service worker (public/sw.js → served at /sw.js with
// root scope). The worker only intercepts audio MP3 GETs (see sw.js), so it is
// safe to register unconditionally in production.
//
// Registration is best-effort and fully guarded: no service worker support, an
// insecure context, or a registration failure all degrade gracefully — the app
// still works, and audio simply falls back to the in-app cached-blob path
// (cachedObjectUrl) for offline and to plain network streaming online.
//
// DEV: Vite's dev server doesn't serve public/sw.js the way the production
// build does, and an SW intercepting dev requests can confuse HMR. We skip
// registration in dev by default; pass force=true to test it locally against a
// production build (`vite preview`).
// ─────────────────────────────────────────────────────────────────────────

export function registerAudioServiceWorker(force = false): void {
	// Only in browsers that support it and on a secure origin (https or
	// localhost). navigator.serviceWorker is undefined otherwise.
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
		return;
	}

	// Skip in Vite dev unless explicitly forced (see header note).
	if (import.meta.env.DEV && !force) {
		return;
	}

	// Register after the window load event so SW work never competes with the
	// initial render/paint.
	const register = (): void => {
		navigator.serviceWorker
			.register('/sw.js', { scope: '/' })
			.catch((err: unknown) => {
				// Non-fatal: log in dev, swallow in prod.
				if (import.meta.env.DEV) {
					console.warn('[audio-sw] registration failed', err);
				}
			});
	};

	if (document.readyState === 'complete') {
		register();
	} else {
		window.addEventListener('load', register, { once: true });
	}
}
